import { timestampDate } from '@bufbuild/protobuf/wkt';
import { UserStatus } from '@sunstone/api-client/gen/tenants/v1/tenants_pb';
import {
  useDeleteUser,
  useGetRole,
  useGetUser,
  useInfiniteListAuditEvents,
  useListAccessRules,
  useListRoleAccessRules,
  useResendInvitation,
  useRevokeInvitation,
  useUpdateUserStatus,
} from '@sunstone/api-client/hooks';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@sunstone/ui';
import { ChevronDown, Loader2, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { AuditHistory } from '@/components/ui/AuditHistory';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useAuthToken } from '@/features/auth';
import { useAuthorization } from '@/features/auth/useAuthorization';
import { AccessSummary } from '@/features/users/AccessSummary';
import { splitRulesByKind } from '@/features/users/accessRuleDraft';
import { useIsPartnerTenant, useTenantName } from '@/features/users/accessRuleSentence';
import { permissionLabel } from '@/lib/permissions';
import { routes } from '@/lib/routes';
import { formatDateTime } from '@/lib/time';
import { deferDialogOpen } from '@/lib/utils';

type Tab = 'role' | 'accessRules' | 'history';

function userStatusKey(status: UserStatus): string {
  switch (status) {
    case UserStatus.INVITED:
      return 'USER_STATUS_INVITED';
    case UserStatus.ACTIVE:
      return 'USER_STATUS_ACTIVE';
    case UserStatus.DISABLED:
      return 'USER_STATUS_DISABLED';
    case UserStatus.DELETED:
      return 'USER_STATUS_DELETED';
    default:
      return 'USER_STATUS_UNSPECIFIED';
  }
}

export function UserDetailPage() {
  const { tenantId, userId } = useParams<{ tenantId: string; userId: string }>();
  const navigate = useNavigate();
  const { token } = useAuthToken();
  const { canManageAccess } = useAuthorization();
  const tenantName = useTenantName(tenantId ?? '');
  const isPartner = useIsPartnerTenant(tenantId ?? '');
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as Tab | null;
  const activeTab: Tab = tabParam === 'history' || tabParam === 'accessRules' ? tabParam : 'role';

  const { data: userData, isLoading: userLoading } = useGetUser(userId ?? '', token);
  const user = userData?.user;

  const { data: rulesData, isLoading: rulesLoading } = useListAccessRules(userId ?? '', token);
  const rules = rulesData?.rules ?? [];
  const { deployment: deploymentRules, workspace: workspaceRules } = splitRulesByKind(rules);

  const { data: roleData } = useGetRole(user?.primaryRoleId ?? '', token);
  const role = roleData?.role;
  const { data: roleRulesData } = useListRoleAccessRules(
    role && role.scope !== 'system' ? role.id : '',
    token,
  );
  const { deployment: roleDeploymentRules, workspace: roleWorkspaceRules } = splitRulesByKind(
    roleRulesData?.accessRules ?? [],
  );

  const {
    data: auditData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteListAuditEvents(token, 'user', userId);
  const auditEvents = auditData?.pages.flatMap((p) => p.events) ?? [];

  const resendInvitation = useResendInvitation(token);
  const revokeInvitation = useRevokeInvitation(token);
  const updateUserStatus = useUpdateUserStatus(token);
  const deleteUser = useDeleteUser(token);

  const [revokeInviteOpen, setRevokeInviteOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (userLoading || !user) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  const isInvited = user.status === UserStatus.INVITED;
  const isActive = user.status === UserStatus.ACTIVE;
  const isDisabled = user.status === UserStatus.DISABLED;
  const isDeleted = user.status === UserStatus.DELETED;

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <PageHeader
        title={user.name || user.email}
        description={user.email}
        action={
          <div className="flex items-center gap-2">
            {canManageAccess && !isDeleted && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    More Actions
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {isInvited && (
                    <>
                      <DropdownMenuItem
                        disabled={resendInvitation.isPending}
                        onClick={() => {
                          resendInvitation.mutate(
                            { userId: user.id },
                            {
                              onSuccess: () => toast.success(`Invitation resent to ${user.email}`),
                              onError: (err) =>
                                toast.error(err.message ?? 'Failed to resend invitation.'),
                            },
                          );
                        }}
                      >
                        Resend Invitation
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={() => deferDialogOpen(() => setRevokeInviteOpen(true))}
                      >
                        Revoke Invitation
                      </DropdownMenuItem>
                    </>
                  )}
                  {isActive && (
                    <DropdownMenuItem
                      disabled={updateUserStatus.isPending}
                      onClick={() => {
                        updateUserStatus.mutate(
                          { id: user.id, status: UserStatus.DISABLED },
                          {
                            onSuccess: () => toast.success(`${user.email} disabled`),
                            onError: (err) => toast.error(err.message ?? 'Failed to disable user.'),
                          },
                        );
                      }}
                    >
                      Disable User
                    </DropdownMenuItem>
                  )}
                  {isDisabled && (
                    <>
                      <DropdownMenuItem
                        disabled={updateUserStatus.isPending}
                        onClick={() => {
                          updateUserStatus.mutate(
                            { id: user.id, status: UserStatus.ACTIVE },
                            {
                              onSuccess: () => toast.success(`${user.email} enabled`),
                              onError: (err) =>
                                toast.error(err.message ?? 'Failed to enable user.'),
                            },
                          );
                        }}
                      >
                        Enable User
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={() => deferDialogOpen(() => setDeleteOpen(true))}
                      >
                        Delete User
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {canManageAccess && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  navigate(
                    tenantId
                      ? routes.editUserAccess(tenantId, user.id)
                      : routes.editPlatformUserAccess(user.id),
                  )
                }
              >
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
            )}
          </div>
        }
      />

      <Card>
        <CardContent className="py-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Status</p>
              <StatusBadge status={userStatusKey(user.status)} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Identity Provider</p>
              <p className="text-sm text-foreground">{user.idpKind || '—'}</p>
            </div>
            {tenantId && (
              <div>
                <p className="text-xs text-muted-foreground mb-1 font-medium">Tenant</p>
                <p className="text-sm font-mono text-foreground truncate">{tenantId}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Created</p>
              <p className="text-sm text-foreground">
                {user.createdAt ? formatDateTime(timestampDate(user.createdAt)) : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Updated</p>
              <p className="text-sm text-foreground">
                {user.updatedAt ? formatDateTime(timestampDate(user.updatedAt)) : '—'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={(v) => setSearchParams({ tab: v })}>
        <TabsList>
          <TabsTrigger value="role">Role</TabsTrigger>
          <TabsTrigger value="accessRules">Access rules</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="role" className="space-y-6 mt-6 animate-in fade-in duration-300">
          <Card>
            <CardContent className="pt-6 space-y-4">
              {user.primaryRoleId && role ? (
                <>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{role.name}</span>
                      <Badge variant="outline" className="text-[10px]">
                        {role.grantsAll ? 'Always current' : 'Fixed list'}
                      </Badge>
                    </div>
                    {role.description && (
                      <p className="text-sm text-muted-foreground mt-1">{role.description}</p>
                    )}
                  </div>
                  {role.grantsAll ? (
                    <p className="text-sm text-muted-foreground">
                      Grants every permission, including any added in the future.
                    </p>
                  ) : role.permissions.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No permissions assigned.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {role.permissions.map((p) => (
                        <Badge key={p} variant="outline">
                          {permissionLabel(p)}
                        </Badge>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No primary role — access rules only.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="accessRules" className="space-y-6 mt-6 animate-in fade-in duration-300">
          <Card>
            <CardContent className="pt-6">
              {rulesLoading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              ) : (
                <AccessSummary
                  mode="role-and-user"
                  tenantId={tenantId ?? ''}
                  tenantName={tenantName}
                  isPartner={isPartner}
                  hasRole={!!user.primaryRoleId}
                  roleDeploymentRules={roleDeploymentRules}
                  roleWorkspaceRules={roleWorkspaceRules}
                  userDeploymentRules={deploymentRules}
                  userWorkspaceRules={workspaceRules}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-6 animate-in fade-in duration-300">
          <AuditHistory
            events={auditEvents}
            onLoadMore={fetchNextPage}
            hasMore={hasNextPage}
            isLoadingMore={isFetchingNextPage}
          />
        </TabsContent>
      </Tabs>

      <AlertDialog open={revokeInviteOpen} onOpenChange={setRevokeInviteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke invitation?</AlertDialogTitle>
            <AlertDialogDescription>
              {user.email} won't be able to accept this invitation anymore.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                revokeInvitation.mutate(
                  { userId: user.id },
                  {
                    onSuccess: () => {
                      toast.success('Invitation revoked');
                      navigate(tenantId ? routes.tenant(tenantId) : routes.platformUsers());
                    },
                    onError: (err) => toast.error(err.message ?? 'Failed to revoke invitation.'),
                  },
                );
              }}
            >
              Revoke Invitation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              {user.email} will be permanently deleted. This cannot be undone. Past audit history is
              kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteUser.mutate(
                  { id: user.id },
                  {
                    onSuccess: (data) => {
                      if (data.warning) {
                        toast.warning(data.warning);
                      } else {
                        toast.success('User deleted');
                      }
                      navigate(tenantId ? routes.tenant(tenantId) : routes.platformUsers());
                    },
                    onError: (err) => toast.error(err.message ?? 'Failed to delete user.'),
                  },
                );
              }}
            >
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
